import { createDictationGroup } from '@/components/tiptap/sharedExtensions/slash-command/dictation-group';
import { createProtocolGroup } from '@/components/tiptap/sharedExtensions/slash-command/protocol-group';
import { createSnippetGroup } from '@/components/tiptap/sharedExtensions/slash-command/snippet-group';
import {
  type Command,
  type Group,
} from '@/components/tiptap/sharedExtensions/slash-command/types';
import { fixCursorIfSafari } from '@/components/tiptap/utils/fixSafariCursor';
import {
  getSectionEditorType,
  trackSectionCreated,
} from '@/components/tiptap/vMarkdown/extensions/section-node/section-analytics';
import { isScribeBlockEditorCodeblockEnabled } from '@/features/flags';
import { simpleMarkdownToHtml } from '@/helpers/markdownToHtml';
import type { TranslationKeys } from '@/types/translation-keys.generated';
import { TypographyP2 } from '@heidi/design-system/typography';
import { insertDraftSectionAt } from '@heidi/editor/core';
import { type Editor } from '@tiptap/react';
import i18next from 'i18next';
import { Code, Minus, PlusSquare, Table } from 'lucide-react';
import { FormattedMessage } from 'react-intl';

const NEW_SECTION_COMMAND_LABEL_KEY =
  'common.vMarkdown.section.newSectionCommandLabel' satisfies TranslationKeys;

export function getInsertCommands(): Command[] {
  return [
    {
      name: 'horizontalRule',
      label: 'Horizontal Rule',
      icon: Minus,
      action: (ed: Editor) => {
        ed.chain().focus().setHorizontalRule().run();
      },
      content: (
        <TypographyP2>
          <FormattedMessage defaultMessage="Horizontal Rule" id="1uhB/CqNka" />
        </TypographyP2>
      ),
    },
    ...(isScribeBlockEditorCodeblockEnabled()
      ? [
          {
            name: 'codeBlock',
            label: 'Code block',
            icon: Code,
            action: (ed: Editor) => {
              ed.chain().focus().toggleCodeBlock().run();
            },
            content: (
              <TypographyP2>
                <FormattedMessage defaultMessage="Code block" id="8typ4gfVt/" />
              </TypographyP2>
            ),
          },
        ]
      : []),
    {
      name: 'table',
      label: 'Table',
      icon: Table,
      action: (ed: Editor) => {
        ed.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
      },
      content: (
        <TypographyP2>
          <FormattedMessage defaultMessage="Table" id="IeBejt65iX" />
        </TypographyP2>
      ),
    },
  ];
}

export function getDefaultGroups(editor: Editor): Group[] {
  const groups: Group[] = [];

  const dictationGroup = createDictationGroup(editor);
  if (dictationGroup) {
    groups.push(dictationGroup);
  }

  groups.push({
    name: 'insert',
    title: 'INSERT',
    commands: [
      ...('sectionNode' in editor.state.schema.nodes
        ? [
            {
              name: 'newSection',
              label: i18next.t(NEW_SECTION_COMMAND_LABEL_KEY),
              icon: PlusSquare,
              action: (ed: Editor) => {
                const { $from } = ed.state.selection;

                const insertPos =
                  $from.depth >= 1 && $from.node(1).type.name === 'sectionNode'
                    ? $from.after(1)
                    : $from.pos;

                insertDraftSectionAt(ed, insertPos);
                trackSectionCreated(getSectionEditorType(ed), 'slash_command');
              },
              content: (
                <TypographyP2>{i18next.t(NEW_SECTION_COMMAND_LABEL_KEY)}</TypographyP2>
              ),
            },
          ]
        : []),
      ...getInsertCommands(),
    ],
  });

  return groups;
}

export function getGroups(editor: Editor) {
  const snippets = createSnippetGroup({
    editor,
    createInsertAction: (ed, content) => {
      if (content.includes('\n')) {
        ed.commands.insertContent(simpleMarkdownToHtml({ markdown: content }));
      } else {
        ed.commands.insertContent(content);
      }
      fixCursorIfSafari(ed);
    },
  });

  // Protocol group - inserts /shorthand at cursor for chat invocation
  const protocols = createProtocolGroup({
    editor,
    createInsertAction: (ed, shorthand) => {
      // Insert the protocol shorthand as a slash command
      ed.commands.insertContent(`/${shorthand} `);
      fixCursorIfSafari(ed);
    },
  });

  const groups = [];
  if (protocols) {
    groups.unshift(protocols);
  }
  groups.push(...getDefaultGroups(editor), snippets);
  return groups;
}
